import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { getAccessToken } from 'utilities/base-auth';
import { parseServerError } from 'helpers/errorHelpers';
import { Context } from 'components/Store';
import { CROPWISE_PROXY_V2_URL } from 'utilities/apiConstants';
import { PestOption } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { chunk } from 'lodash';

// Constants
const DEFAULT_PESTS_CATALOG_LIMIT = 1000;

// Type definitions
export interface Organization {
  id: string;
  [key: string]: any;
}

export interface PestsApiResponse {
  data: PestOption[];
  [key: string]: any;
}

export interface UsePestsDataV2Options {
  minSearchTextLength?: number;
  page?: number;
  pestId?: string | null;
  autoSearch?: boolean;
  limit?: number;
}

export interface UsePestsDataV2Return {
  pests: PestOption[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  currentPage: number;
  searchPests: (
    customParams?: Record<string, any>
  ) => Promise<AxiosResponse<PestsApiResponse>>;
  getPestById: (id: string) => Promise<AxiosResponse<PestsApiResponse>>;
  getPestsByNameIds: (nameIds: string[]) => Promise<PestOption[]>;
}

interface PestsCatalogParams {
  language?: string;
  page?: number;
  limit?: number;
  name?: string;
  pestId?: string;
  [key: string]: any;
}

interface ContextState {
  organization: Organization;
  [key: string]: any;
}

// Create a new axios instance for the pests catalog API
const createPestsCatalogAxios = (organization: Organization): AxiosInstance => {
  const instance = axios.create({
    baseURL: CROPWISE_PROXY_V2_URL,
    timeout: 10000
  });

  // Add request interceptor to include auth header and organization context
  instance.interceptors.request.use(
    config => {
      const token = getAccessToken();
      if (token && organization) {
        // set() rather than reassigning: axios 1.x types config.headers as an
        // AxiosHeaders instance, and replacing it with a plain object strips
        // the methods the rest of the pipeline calls on it.
        config.headers.set('Content-Type', 'application/json');
        config.headers.set(
          'cwf-context',
          JSON.stringify({ organization_id: organization.id })
        );
        config.headers.set('Authorization', `Bearer ${token}`);
      }
      return config;
    },
    error =>
      Promise.reject(error instanceof Error ? error : new Error(String(error)))
  );

  return instance;
};

const getPestsCatalogV2 = (
  organization: Organization,
  params: PestsCatalogParams = {}
): Promise<AxiosResponse<PestsApiResponse>> => {
  const searchParams = new URLSearchParams();

  // Set default parameters
  const defaultParams: PestsCatalogParams = {
    language: 'en',
    page: 0,
    limit: DEFAULT_PESTS_CATALOG_LIMIT,
    ...params
  };

  // Add all non-empty parameters to the search params
  Object.entries(defaultParams).forEach(([key, value]) => {
    if (value != null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const pestsAxios = createPestsCatalogAxios(organization);
  return pestsAxios.get(`/pests?${searchParams.toString()}`);
};

const usePestsDataV2 = (
  searchText: string,
  options: UsePestsDataV2Options = {}
): UsePestsDataV2Return => {
  const [{ organization }, dispatch] = (useContext(Context) as unknown) as [
    ContextState,
    any
  ];
  const [pests, setPests] = useState<PestOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const currentRequestIdRef = useRef<number>(0);

  const {
    minSearchTextLength = 3,
    page = 0,
    pestId = null,
    autoSearch = true,
    limit = DEFAULT_PESTS_CATALOG_LIMIT
  } = options;

  // Helper function to check if request is still current
  const isRequestCurrent = useCallback(
    (requestId: number): boolean => currentRequestIdRef.current === requestId,
    []
  );

  // Helper function to process response data
  const processResponseData = useCallback(
    (responseData: PestsApiResponse, pageNum: number): void => {
      const pestsArray = responseData.data || responseData;
      const totalElements = responseData.elements ?? 0;

      if (Array.isArray(pestsArray)) {
        setPests(pestsArray);
        setTotalCount(totalElements);
        setCurrentPage(pageNum);
      } else {
        setPests([]);
        setTotalCount(0);
        setCurrentPage(pageNum);
      }
    },
    []
  );

  // Helper function to handle errors
  const handleError = useCallback(
    (err: any, requestId: number, shouldThrow = false): void => {
      // Skip processing if request is no longer current
      if (!isRequestCurrent(requestId)) {
        if (shouldThrow) throw err;
        return;
      }

      // For canceled requests, just set loading to false and ignore silently
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        setLoading(false);
        if (shouldThrow) throw err;
        return;
      }

      // Handle actual errors
      setError(err);
      parseServerError(dispatch)(err);
      setPests([]);
      setTotalCount(0);
      setLoading(false);

      if (shouldThrow) throw err;
    },
    [dispatch, isRequestCurrent]
  );

  useEffect(() => {
    const trimmedSearchText = (searchText ?? '').trim();

    // Increment request ID to invalidate previous requests
    currentRequestIdRef.current += 1;
    const currentRequestId = currentRequestIdRef.current;

    // Reset pagination to page 0 for new searches
    setCurrentPage(0);

    // Only auto-search if autoSearch is true and search text meets minimum length
    if (!autoSearch || trimmedSearchText.length < minSearchTextLength) {
      setLoading(false);
      if (!autoSearch && trimmedSearchText.length === 0) {
        setPests([]);
        setTotalCount(0);
      }
      return;
    }

    setLoading(true);
    setError(null);

    const params: PestsCatalogParams = {
      page,
      limit,
      language: 'en'
    };

    if (trimmedSearchText) {
      params.name = trimmedSearchText;
    }

    if (pestId) {
      params.pestId = pestId;
    }

    getPestsCatalogV2(organization, params)
      .then(response => {
        if (!isRequestCurrent(currentRequestId)) return;

        processResponseData(response.data, page);
        setLoading(false);
      })
      .catch(err => handleError(err, currentRequestId));
  }, [
    dispatch,
    searchText,
    minSearchTextLength,
    page,
    pestId,
    autoSearch,
    organization,
    handleError,
    isRequestCurrent,
    processResponseData,
    limit
  ]);

  // Manual search function for when autoSearch is disabled
  const searchPests = useCallback(
    (
      customParams: Record<string, any> = {}
    ): Promise<AxiosResponse<PestsApiResponse>> => {
      // Increment request ID
      currentRequestIdRef.current += 1;
      const currentRequestId = currentRequestIdRef.current;

      setLoading(true);
      setError(null);

      const params: PestsCatalogParams = {
        page: 0,
        limit: DEFAULT_PESTS_CATALOG_LIMIT,
        language: 'en',
        ...customParams
      };

      return getPestsCatalogV2(organization, params)
        .then(response => {
          if (!isRequestCurrent(currentRequestId)) {
            return response;
          }

          processResponseData(response.data, params.page || 0);
          setLoading(false);
          return response;
        })
        .catch(err => {
          handleError(err, currentRequestId, true);
          throw err; // Re-throw for the caller
        });
    },
    [organization, isRequestCurrent, processResponseData, handleError]
  );

  // Utility function to get pest by ID
  const getPestById = useCallback((id: string) => searchPests({ pestId: id }), [
    searchPests
  ]);

  // Function to get pests by name IDs using POST /pests/list
  const getPestsByNameIdsByPage = useCallback(
    (nameIds: string[]): Promise<AxiosResponse<PestOption[]>> => {
      // Increment request ID
      currentRequestIdRef.current += 1;
      const currentRequestId = currentRequestIdRef.current;

      setLoading(true);
      setError(null);

      const pestsAxios = createPestsCatalogAxios(organization);

      return pestsAxios
        .post('/pests/list', { nameIds })
        .then(response => {
          if (!isRequestCurrent(currentRequestId)) {
            return response;
          }

          setLoading(false);
          return response;
        })
        .catch(err => {
          handleError(err, currentRequestId, true);
          throw err; // Re-throw for the caller
        });
    },
    [organization, isRequestCurrent, handleError]
  );

  const getPestsByNameIds = useCallback(
    async (nameIds: string[]): Promise<PestOption[]> => {
      const uniquePestNameIds = Array.from(new Set(nameIds));
      const chunkedPestNameIds = chunk(uniquePestNameIds, 50);
      const pestResponses = await Promise.all(
        chunkedPestNameIds.map(partialChunk =>
          getPestsByNameIdsByPage(partialChunk)
        )
      );
      return pestResponses.flatMap(response => response.data);
    },
    [getPestsByNameIdsByPage]
  );

  return {
    pests,
    loading,
    error,
    totalCount,
    currentPage,
    searchPests,
    getPestById,
    getPestsByNameIds
  };
};

export default usePestsDataV2;
